<template>
    <modal id="document-image-editor-modal"
           size="extra-large"
           v-model="isActive"
           :title="modalTitle"
           :hideFooter="true"
            :preloader="preloader"
           body-class="document-image-editor-body">
        <div class="document-image-editor">
            <div class="document-image-editor-toolbar">
                <div class="form-group mb-0 document-image-editor-range">
                    <label class="mb-1">{{ $t('blur') }}</label>
                    <input type="range"
                           class="custom-range"
                           min="1"
                           max="100"
                           step="1"
                           v-model.number="blurRadius"
                           @input="updateBlur"
                           @change="commitHistory">
                    <small class="text-muted">{{ blurRadius }}px</small>
                </div>

                <div class="form-group mb-0 document-image-editor-range">
                    <label class="mb-1">{{ $t('size') }}</label>
                    <input type="range"
                           class="custom-range"
                           min="10"
                           max="400"
                           step="5"
                           v-model.number="areaSize"
                           @input="updateAreaSize"
                           @change="commitHistory">
                    <small class="text-muted">{{ areaSize }}px</small>
                </div>
            </div>

            <div v-if="loadError" class="alert alert-danger mb-0">{{ loadError }}</div>

            <div v-show="!loadError" class="document-image-editor-stage-wrap">
                <div ref="stageContainer" class="document-image-editor-stage"></div>
            </div>

            <div class="document-image-editor-footer">
                <button type="button"
                        class="btn btn-secondary"
                        :disabled="!canUndo || saving"
                        @click="undo">
                    {{ $t('undo') }}
                </button>
                <button type="button"
                        class="btn btn-secondary"
                        :disabled="!canRedo || saving"
                        @click="redo">
                    {{ $t('redo') }}
                </button>
                <button type="button"
                        class="btn btn-warning"
                        :disabled="saving"
                        @click="resetEditor">
                    {{ $t('reset') }}
                </button>
                <button type="button"
                        class="btn btn-primary"
                        :disabled="saving || !!loadError"
                        @click="saveImage">
                    <span v-if="saving" class="spinner-border spinner-border-sm mr-1"></span>
                    {{ $t('save') }}
                </button>
            </div>
        </div>
    </modal>
</template>

<script>
import Konva from 'konva';
import {urlGenerator} from "../../../../../../common/Helper/AxiosHelper";
import HelperMixin from "../../../../../../common/Mixin/Global/HelperMixin";

export default {
    name  : "DocumentImageEditorModal",
    mixins: [HelperMixin],
    props : {
        value: {
            type: Boolean,
            default: false
        },
        files: {
            type   : Array,
            default: () => []
        },
        rowData: {
            type   : Object,
            default: () => ({})
        },
        selectedPath: {
            type   : String,
            default: ''
        },
        saving: {
            type   : Boolean,
            default: false
        }
    },
    data() {
        return {
            preloader     : true,
            loadError     : '',
            blurRadius    : 14,
            areaSize      : 140,
            initialAreaSize: 100,
            committedRegions: [],
            history       : [],
            historyIndex  : -1,
            stage         : null,
            baseLayer     : null,
            effectLayer   : null,
            imageNode     : null,
            blurredNode   : null,
            blurGroup     : null,
            shapeNode     : null,
            transformer   : null,
            imageElement  : null,
            displayWidth  : 0,
            displayHeight : 0,
            stageScale    : 1,
            maxCanvasWidth: 900,
            maxCanvasHeight: 560,
        }
    },
    computed: {
        isActive: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value);
            }
        },
        imageFiles() {
            return this.files.filter(file => this.isImage(file));
        },
        currentPath() {
            return this.selectedPath || this.imageFiles[0] || '';
        },
        currentUrl() {
            return this.currentPath ? urlGenerator(this.currentPath) : '';
        },
        currentFileName() {
            return this.currentPath ? this.getFileName(this.currentPath) : '';
        },
        modalTitle() {
            return this.currentFileName ? `${this.$t('edit_image')}: ${this.currentFileName}` : this.$t('edit_image');
        },
        canUndo() {
            return this.historyIndex > 0;
        },
        canRedo() {
            return this.historyIndex < this.history.length - 1;
        },
        maxAreaSize() {
            const size = Math.min(this.displayWidth || this.maxCanvasWidth, this.displayHeight || this.maxCanvasHeight);
            return Math.min(400, Math.max(10, Math.floor(size)));
        }
    },
    watch: {
        value(isActive) {
            if (isActive) {
                this.prepareEditor();
            } else {
                this.destroyStage();
            }
        }
    },
    mounted() {
        if (this.value) {
            this.prepareEditor();
        }
    },
    beforeDestroy() {
        this.destroyStage();
    },
    methods: {
        prepareEditor() {
            this.resetOptions();
            this.$nextTick(() => this.loadImage());
        },
        resetOptions() {
            this.blurRadius = 15;
            this.areaSize = this.initialAreaSize;
            this.committedRegions = [];
            this.loadError = '';
            this.history = [];
            this.historyIndex = -1;
        },
        loadImage() {
            this.destroyStage();
            this.preloader = true;
            this.loadError = '';

            if (!this.currentUrl) {
                this.loadError = this.$t('no_image_to_edit');
                this.preloader = false;
                return;
            }

            const image = new Image();
            image.crossOrigin = 'Anonymous';
            image.onload = () => {
                this.imageElement = image;
                this.preloader = false;
                this.$nextTick(() => this.createStage());
            };
            image.onerror = () => {
                this.loadError = this.$t('failed_to_load_image');
                this.preloader = false;
            };
            image.src = this.currentUrl;
        },
        createStage() {
            const ratio = Math.min(
                this.maxCanvasWidth / this.imageElement.naturalWidth,
                this.maxCanvasHeight / this.imageElement.naturalHeight,
                1
            );

            this.stageScale = ratio;
            this.displayWidth = Math.round(this.imageElement.naturalWidth * ratio);
            this.displayHeight = Math.round(this.imageElement.naturalHeight * ratio);
            this.initialAreaSize = Math.min(140, this.maxAreaSize);
            this.areaSize = Math.min(this.areaSize, this.maxAreaSize);

            this.stage = new Konva.Stage({
                container: this.$refs.stageContainer,
                width    : this.displayWidth,
                height   : this.displayHeight,
            });
            this.stage.on('mousedown tap', this.handleStagePointerDown);

            this.baseLayer = new Konva.Layer();
            this.effectLayer = new Konva.Layer();
            this.stage.add(this.baseLayer);
            this.stage.add(this.effectLayer);

            this.imageNode = new Konva.Image({
                image : this.imageElement,
                width : this.displayWidth,
                height: this.displayHeight,
            });
            this.baseLayer.add(this.imageNode);

            this.blurredNode = new Konva.Image({
                image : this.imageElement,
                width : this.displayWidth,
                height: this.displayHeight,
            });
            this.blurredNode.cache();
            this.blurredNode.filters([Konva.Filters.Blur]);
            this.blurredNode.blurRadius(this.blurRadius);

            this.blurGroup = new Konva.Group({
                clipFunc: context => this.drawClip(context)
            });
            this.blurGroup.add(this.blurredNode);
            this.effectLayer.add(this.blurGroup);

            this.createShape();
            this.transformer = new Konva.Transformer({
                nodes        : [this.shapeNode],
                rotateEnabled: false,
                keepRatio    : false,
                flipEnabled  : false,
                enabledAnchors: ['top-left', 'top-right', 'bottom-left', 'bottom-right'],
                borderEnabled: false,
                anchorFill   : '#ffffff',
                anchorStroke : '#1d4ed8',
                anchorStrokeWidth: 1,
                anchorSize   : 5,
                anchorCornerRadius: 5,
                padding      : 0,
                boundBoxFunc : (oldBox, newBox) => {
                    const hasInvalidNumber = [newBox.x, newBox.y, newBox.width, newBox.height]
                        .some(value => !Number.isFinite(value));

                    if (hasInvalidNumber || newBox.width < 10 || newBox.height < 10) {
                        return oldBox;
                    }

                    return newBox;
                }
            });
            this.effectLayer.add(this.transformer);

            this.initHistory();
            this.baseLayer.draw();
            this.effectLayer.draw();
        },
        createShape() {
            if (this.shapeNode) {
                this.shapeNode.destroy();
            }

            const centerX = this.displayWidth / 2;
            const centerY = this.displayHeight / 2;
            const config = {
                x        : centerX - this.areaSize / 2,
                y        : centerY - this.areaSize / 2,
                draggable: true,
                width    : this.areaSize,
                height   : this.areaSize,
                stroke   : '#1d4ed8',
                strokeWidth: 1,
                strokeScaleEnabled: false,
                fill     : 'rgba(29, 78, 216, 0.08)',
            };

            this.shapeNode = new Konva.Rect(config);
            this.shapeNode.on('dragmove', () => this.effectLayer.batchDraw());
            this.shapeNode.on('transform', () => this.effectLayer.batchDraw());
            this.shapeNode.on('dragend transformend', () => {
                this.finalizeTransform();
                this.syncAreaSizeFromShape();
                this.commitHistory();
                this.setStageCursor('grab');
                this.effectLayer.batchDraw();
            });
            this.shapeNode.on('mouseover', () => this.setStageCursor('grab'));
            this.shapeNode.on('mouseout', () => this.setStageCursor('default'));
            this.shapeNode.on('mousedown touchstart dragstart', () => this.setStageCursor('grabbing'));
            this.shapeNode.on('mouseup touchend', () => this.setStageCursor('grab'));
            this.effectLayer.add(this.shapeNode);

            if (this.transformer) {
                this.transformer.nodes([this.shapeNode]);
            }
        },
        drawClip(context) {
            if (!this.shapeNode) {
                return;
            }

            const regions = [...this.committedRegions, this.getShapeBox()];

            context.beginPath();
            regions.forEach(region => {
                context.rect(region.x, region.y, region.width, region.height);
            });
            context.closePath();
        },
        getShapeBox() {
            const rawWidth = Number(this.shapeNode.width()) || 0;
            const rawHeight = Number(this.shapeNode.height()) || 0;
            const scaleX = Number(this.shapeNode.scaleX()) || 1;
            const scaleY = Number(this.shapeNode.scaleY()) || 1;
            const x = Number(this.shapeNode.x()) || 0;
            const y = Number(this.shapeNode.y()) || 0;
            const width = rawWidth * scaleX;
            const height = rawHeight * scaleY;

            return {
                x     : width < 0 ? x + width : x,
                y     : height < 0 ? y + height : y,
                width : Math.abs(width),
                height: Math.abs(height),
            };
        },
        updateBlur() {
            if (!this.blurredNode) {
                return;
            }

            this.blurredNode.blurRadius(this.blurRadius);
            this.blurredNode.cache();
            this.effectLayer.batchDraw();
        },
        updateAreaSize() {
            if (!this.shapeNode) {
                return;
            }

            const box = this.getShapeBox();
            const centerX = box.x + box.width / 2;
            const centerY = box.y + box.height / 2;
            const nextSize = Math.min(Math.max(this.areaSize, 10), this.maxAreaSize);

            this.areaSize = nextSize;
            this.shapeNode.size({width: nextSize, height: nextSize});
            this.shapeNode.scale({x: 1, y: 1});
            this.shapeNode.position({x: centerX - nextSize / 2, y: centerY - nextSize / 2});

            this.transformer.forceUpdate();
            this.effectLayer.batchDraw();
        },
        finalizeTransform() {
            if (!this.shapeNode) {
                return;
            }

            const box = this.getShapeBox();

            if (!Number.isFinite(box.x) || !Number.isFinite(box.y) || !Number.isFinite(box.width) || !Number.isFinite(box.height)) {
                return;
            }

            this.shapeNode.size({
                width : box.width,
                height: box.height
            });
            this.shapeNode.position({x: box.x, y: box.y});
            this.shapeNode.scale({x: 1, y: 1});
            this.transformer.forceUpdate();
        },
        handleStagePointerDown(event) {
            if (!this.stage || !this.shapeNode || !this.transformer) {
                return;
            }

            const target = event.target;
            const clickedSelection = target === this.shapeNode;
            const clickedTransformer = target && target.getParent && target.getParent() === this.transformer;

            if (clickedSelection || clickedTransformer) {
                return;
            }

            const pointer = this.stage.getPointerPosition();

            if (!pointer) {
                return;
            }

            this.commitCurrentRegion();
            this.moveSelectionToPointer(pointer);
            this.commitHistory();
            this.effectLayer.batchDraw();
        },
        commitCurrentRegion() {
            const region = this.getShapeBox();
            const exists = this.committedRegions.some(item =>
                item.x === region.x &&
                item.y === region.y &&
                item.width === region.width &&
                item.height === region.height
            );

            if (exists) {
                return;
            }

            this.committedRegions.push({
                x     : region.x,
                y     : region.y,
                width : region.width,
                height: region.height,
            });
        },
        moveSelectionToPointer(pointer) {
            const width = Number(this.shapeNode.width()) || this.areaSize;
            const height = Number(this.shapeNode.height()) || this.areaSize;
            const nextX = Math.min(
                Math.max(pointer.x - width / 2, 0),
                Math.max(this.displayWidth - width, 0)
            );
            const nextY = Math.min(
                Math.max(pointer.y - height / 2, 0),
                Math.max(this.displayHeight - height, 0)
            );

            this.shapeNode.position({x: nextX, y: nextY});
            this.shapeNode.scale({x: 1, y: 1});
            this.transformer.forceUpdate();
        },
        resetEditor() {
            this.resetOptions();
            this.destroyStage();
            this.$nextTick(() => this.createStage());
        },
        syncAreaSizeFromShape() {
            const box = this.getShapeBox();
            this.areaSize = Math.round(Math.max(box.width, box.height));
        },
        initHistory() {
            this.history = [this.getCurrentState()];
            this.historyIndex = 0;
        },
        commitHistory() {
            if (!this.shapeNode) {
                return;
            }

            const state = this.getCurrentState();
            const currentState = this.history[this.historyIndex];

            if (currentState && JSON.stringify(currentState) === JSON.stringify(state)) {
                return;
            }

            this.history = this.history.slice(0, this.historyIndex + 1);
            this.history.push(state);
            this.historyIndex = this.history.length - 1;
        },
        getCurrentState() {
            const box = this.getShapeBox();

            return {
                x         : Math.round(box.x),
                y         : Math.round(box.y),
                width     : Math.round(box.width),
                height    : Math.round(box.height),
                blurRadius: this.blurRadius,
                committedRegions: this.committedRegions.map(region => ({...region})),
            };
        },
        applyHistoryState(state) {
            if (!state || !this.shapeNode) {
                return;
            }

            this.blurRadius = state.blurRadius;
            this.committedRegions = Array.isArray(state.committedRegions)
                ? state.committedRegions.map(region => ({...region}))
                : [];
            this.areaSize = Math.round(Math.max(state.width, state.height));
            this.shapeNode.position({x: state.x, y: state.y});
            this.shapeNode.size({width: state.width, height: state.height});
            this.shapeNode.scale({x: 1, y: 1});
            this.blurredNode.blurRadius(this.blurRadius);
            this.blurredNode.cache();
            this.transformer.forceUpdate();
            this.effectLayer.batchDraw();
        },
        undo() {
            if (!this.canUndo) {
                return;
            }

            this.historyIndex -= 1;
            this.applyHistoryState(this.history[this.historyIndex]);
        },
        redo() {
            if (!this.canRedo) {
                return;
            }

            this.historyIndex += 1;
            this.applyHistoryState(this.history[this.historyIndex]);
        },
        saveImage() {
            if (!this.stage || !this.currentPath) {
                return;
            }

            this.transformer.visible(false);
            this.shapeNode.visible(false);
            this.effectLayer.draw();

            try {
                const pixelRatio = Math.max(1, this.imageElement.naturalWidth / this.displayWidth);
                const dataUrl = this.stage.toDataURL({
                    mimeType  : 'image/jpeg',
                    quality   : 0.92,
                    pixelRatio: pixelRatio
                });
                const file = this.dataUrlToFile(dataUrl, this.editedFileName());

                this.$emit('save', {
                    file,
                    rowData     : this.rowData
                });
            } catch (error) {
                this.$toastr.e('', this.$t('could_not_export_image'));
            } finally {
                this.shapeNode.visible(true);
                this.transformer.visible(true);
                this.effectLayer.draw();
            }
        },
        editedFileName() {
            const name = this.getFileName(this.currentPath).replace(/\.[^.]+$/, '');
            return `${name}-blur.jpg`;
        },
        dataUrlToFile(dataUrl, fileName) {
            const parts = dataUrl.split(',');
            const mime = parts[0].match(/:(.*?);/)[1];
            const binary = atob(parts[1]);
            const buffer = new Uint8Array(binary.length);

            for (let index = 0; index < binary.length; index++) {
                buffer[index] = binary.charCodeAt(index);
            }

            return new File([buffer], fileName, {type: mime});
        },
        setStageCursor(cursor) {
            if (!this.stage) {
                return;
            }

            this.stage.container().style.cursor = cursor;
        },
        destroyStage() {
            if (this.stage) {
                this.stage.off('mousedown tap', this.handleStagePointerDown);
                this.stage.destroy();
            }

            this.stage = null;
            this.baseLayer = null;
            this.effectLayer = null;
            this.imageNode = null;
            this.blurredNode = null;
            this.blurGroup = null;
            this.shapeNode = null;
            this.transformer = null;
        }
    }
}
</script>

<style scoped>
.document-image-editor {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.document-image-editor-toolbar {
    display: flex;
    align-items: flex-end;
    justify-content: center;
    flex-wrap: wrap;
    gap: 1rem;
}

.document-image-editor-range {
    width: 260px;
    text-align: center;
}

.document-image-editor-stage-wrap {
    overflow: auto;
    padding: 0.75rem;
    background: #f5f7fb;
    border: 1px solid #e1e5ee;
    border-radius: 6px;
}

.document-image-editor-stage {
    display: flex;
    justify-content: center;
    min-height: 180px;
}

.document-image-editor-footer {
    display: flex;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 0.5rem;
}
</style>
